/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { mcpApprovalPromote } from "../funcs/mcpApprovalPromote.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { ApprovalRequestSummary } from "../models/components/approvalrequestsummary.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  PromoteMcpApprovalRequestRequest,
  PromoteMcpApprovalRequestSecurity,
} from "../models/operations/promotemcpapprovalrequest.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type PromoteMcpApprovalRequestMutationVariables = {
  request: PromoteMcpApprovalRequestRequest;
  security?: PromoteMcpApprovalRequestSecurity | undefined;
  options?: RequestOptions;
};

export type PromoteMcpApprovalRequestMutationData = ApprovalRequestSummary;

export type PromoteMcpApprovalRequestMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * promote mcpApproval
 *
 * @remarks
 * Promote a risk-policy bypass request into an approval request, carrying its requester and justification into the review queue.
 */
export function usePromoteMcpApprovalRequestMutation(
  options?: MutationHookOptions<
    PromoteMcpApprovalRequestMutationData,
    PromoteMcpApprovalRequestMutationError,
    PromoteMcpApprovalRequestMutationVariables
  >,
): UseMutationResult<
  PromoteMcpApprovalRequestMutationData,
  PromoteMcpApprovalRequestMutationError,
  PromoteMcpApprovalRequestMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildPromoteMcpApprovalRequestMutation(client, options),
    ...options,
  });
}

export function mutationKeyPromoteMcpApprovalRequest(): MutationKey {
  return ["@gram/client", "mcpApproval", "promote"];
}

export function buildPromoteMcpApprovalRequestMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: PromoteMcpApprovalRequestMutationVariables,
  ) => Promise<PromoteMcpApprovalRequestMutationData>;
} {
  return {
    mutationKey: mutationKeyPromoteMcpApprovalRequest(),
    mutationFn: function promoteMcpApprovalRequestMutationFn({
      request,
      security,
      options,
    }): Promise<PromoteMcpApprovalRequestMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(mcpApprovalPromote(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
